import { faker } from 'mocks/utils/faker';
import {
  IMockFeatureToggleTable,
  IMockProgressiveDeliveryConfigTable,
  IMockProgressiveDeliveryAuditLogTable,
  IMockRolloutStage,
  ProgressiveDeliveryStatus,
} from 'mocks/types/mockDbTables';

/**
 * TOTAL ORGS denominator for the whole PD mock — both the simulated enrollment
 * counts here and the `totalOrgCount` the detail query reports.
 *
 * The BE derives this from the count of rows in the PD Region table, which is
 * effectively every org that has signed in to the portal since the feature
 * deployed to prod on 2026-08-12. That is a much tighter population than AIM's
 * org list, which carries a long tail of orgs onboarded but never signed in to.
 * Observed reality is ~60-70K unique orgs over three months, so this sits at the
 * midpoint.
 *
 * Exported because the mock BE must report the same population it generated
 * enrollment against — reading a different denominator (e.g. the 50-row
 * `organizations` table backing the org picker) yields impossible shares like
 * "185702.0% enrolled".
 */
export const TOTAL_ORGS = 65000;

// Sentinel the BE resolves an unresolvable/removed AIM user to (mirrors
// `ProgressiveDeliveryConstants.DELETED_USER` on the service). AC 5.6.
const DELETED_USER = 'Deleted User';

/**
 * Stage plans the mock draws from. The first mirrors `DEFAULT_STAGES` in
 * `domains/ProgressiveDelivery/utils/stageBuilder.ts` — the 30/50/100 plan
 * Product asked to be the default — so the common case in mock data is the
 * common case in the product. The other two exist to exercise a longer plan and
 * the MIN_STAGES floor.
 */
const STAGE_TEMPLATES: IMockRolloutStage[][] = [
  [
    { percentage: 30, durationDays: 2 },
    { percentage: 50, durationDays: 2 },
    { percentage: 100, durationDays: 0 },
  ],
  [
    { percentage: 25, durationDays: 3 },
    { percentage: 50, durationDays: 2 },
    { percentage: 75, durationDays: 2 },
    { percentage: 100, durationDays: 0 },
  ],
  [
    { percentage: 50, durationDays: 2 },
    { percentage: 100, durationDays: 0 },
  ],
];

// 10 PD configs: 3 active (different stages), 2 paused, 2 inactive, 2 completed, 1 rolled back
export const PD_STATUS_DISTRIBUTION: ProgressiveDeliveryStatus[] = [
  ProgressiveDeliveryStatus.Active,
  ProgressiveDeliveryStatus.Active,
  ProgressiveDeliveryStatus.Active,
  ProgressiveDeliveryStatus.Paused,
  ProgressiveDeliveryStatus.Paused,
  ProgressiveDeliveryStatus.Inactive,
  ProgressiveDeliveryStatus.Inactive,
  ProgressiveDeliveryStatus.Completed,
  ProgressiveDeliveryStatus.Completed,
  ProgressiveDeliveryStatus.RolledBack,
];

const buildConfig = (
  toggle: IMockFeatureToggleTable,
  status: ProgressiveDeliveryStatus
): IMockProgressiveDeliveryConfigTable => {
  const stages = faker.helpers.arrayElement(STAGE_TEMPLATES);

  let currentStageIndex: number;
  if (
    status === ProgressiveDeliveryStatus.Inactive ||
    status === ProgressiveDeliveryStatus.RolledBack
  ) {
    currentStageIndex = 0;
  } else if (status === ProgressiveDeliveryStatus.Completed) {
    currentStageIndex = stages.length - 1;
  } else {
    currentStageIndex = faker.helpers.rangeToNumber({
      min: 0,
      max: stages.length - 2,
    });
  }

  const currentPercentage = stages[currentStageIndex].percentage;
  let simulatedAssignmentCount = 0;
  if (
    status === ProgressiveDeliveryStatus.Active ||
    status === ProgressiveDeliveryStatus.Paused
  ) {
    const target = Math.round((currentPercentage / 100) * TOTAL_ORGS);
    simulatedAssignmentCount = faker.helpers.rangeToNumber({
      min: Math.floor(target * 0.5),
      max: target,
    });
  } else if (status === ProgressiveDeliveryStatus.RolledBack) {
    // Rolled back still had some orgs enrolled before rollback
    const target = Math.round((stages[0].percentage / 100) * TOTAL_ORGS);
    simulatedAssignmentCount = faker.helpers.rangeToNumber({
      min: Math.floor(target * 0.3),
      max: Math.floor(target * 0.8),
    });
  }

  const stageStartedAt =
    status === ProgressiveDeliveryStatus.Active ||
    status === ProgressiveDeliveryStatus.Paused
      ? faker.date.recent({ days: 3 }).toISOString()
      : status === ProgressiveDeliveryStatus.Completed
        ? faker.date.recent({ days: 7 }).toISOString()
        : null;

  return {
    toggleName: toggle.name,
    rolloutStages: stages,
    currentStageIndex,
    stageStartedAt,
    pausedAt:
      status === ProgressiveDeliveryStatus.Paused
        ? faker.date.recent({ days: 1 }).toISOString()
        : null,
    status,
    simulatedAssignmentCount,
    createdAt: faker.date.recent({ days: 30 }).toISOString(),
    createdBy: faker.person.fullName(),
    updatedAt: faker.date.recent({ days: 7 }).toISOString(),
    updatedBy: faker.person.fullName(),
  };
};

export const generateProgressiveDeliveryConfigs = (
  toggles: IMockFeatureToggleTable[]
): IMockProgressiveDeliveryConfigTable[] => {
  const enabledToggles = toggles.filter((t) => t.isEnabled);

  // Invariant guard (AC5): the 3/2/2/2/1 distribution requires at least
  // PD_STATUS_DISTRIBUTION.length enabled toggles to draw from. `isEnabled` is
  // random/unseeded, so a low-enabled seed could previously under-generate and
  // silently drop the tail of the distribution (e.g. ROLLED_BACK). Fail loudly
  // instead of truncating so the mock never ships an incomplete distribution.
  if (enabledToggles.length < PD_STATUS_DISTRIBUTION.length) {
    throw new Error(
      `PD mock needs >= ${PD_STATUS_DISTRIBUTION.length} enabled toggles, got ${enabledToggles.length}`
    );
  }

  const selected = faker.helpers.arrayElements(
    enabledToggles,
    PD_STATUS_DISTRIBUTION.length
  );

  const configs = selected.map((toggle, idx) =>
    buildConfig(toggle, PD_STATUS_DISTRIBUTION[idx])
  );

  // AC 5.6: represent the removed-user degradation in mock data so the config
  // panel exercises the "Deleted User" fallback. Set the sentinel on an
  // existing config (does not change the 10-config status distribution).
  if (configs.length > 0) {
    configs[0].createdBy = DELETED_USER;
    configs[0].updatedBy = DELETED_USER;
  }

  return configs;
};

export const generateProgressiveDeliveryAuditLog = (
  configs: IMockProgressiveDeliveryConfigTable[]
): IMockProgressiveDeliveryAuditLogTable[] => {
  return configs.map((config, idx) => ({
    id: idx + 1,
    toggleName: config.toggleName,
    action: 'CREATED',
    previousValue: null,
    newValue: JSON.stringify({ rolloutStages: config.rolloutStages }),
    performedBy: config.createdBy,
    performedAt: config.createdAt,
  }));
};
